import React, { useState, useEffect } from 'react';
// 1. Import the Rings SVG
import orbitRings from '/assets/crm-360deg-animation-circle.svg';
// 2. Your custom center logo
import travelLogo from '/assets/Travel_Logo-02.svg';

const FloatingLabel = ({ text, positions, delay = 0, isMobile }) => {
    // Select the correct position based on screen size
    const currentPosition = isMobile ? positions.mobile : positions.desktop;

    return (
        <div
            className="absolute z-20 flex items-center justify-center cursor-pointer transition-all duration-300"
            style={{ top: currentPosition.top, left: currentPosition.left, transform: 'translate(-50%, -50%)' }}
        >
            {/* Added rounded-[20px] to make multiline text look like perfect pill shapes */}
            <div className="bg-white px-3 py-1.5 sm:px-4 sm:py-2 md:px-4 md:py-2 xl:px-5 xl:py-2.5 rounded-[20px] md:rounded-full whitespace-nowrap text-center transition-transform duration-300  shadow-md">
                <span className="text-black font-poppins font-semibold text-[15px] sm:text-[11px] md:text-[14px] lg:text-[18px] xl:text-[22px] 2xl:text-[24px] leading-tight tracking-tight">
                    {text.split(/\\n|\n/).map((line, i, arr) => (
                        <React.Fragment key={i}>
                            {line}
                            {i !== arr.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                </span>
            </div>
        </div>
    );
};

const Feature360Section = () => {
    const [isMobile, setIsMobile] = useState(false);

    // Track window resize to toggle between mobile and desktop label coordinates
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768); // 768px matches Tailwind's 'md' breakpoint
        };

        handleResize(); // Check on mount
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Configuration array for all labels. 
    // Added \n to text so it stacks perfectly on mobile like the reference screenshot.
    const labelsData = [
        // Left Column (Mobile Stack)
        { text: "Destination Discovery", positions: { desktop: { top: "28%", left: "18%" }, mobile: { top: "10%", left: "26%" } }, delay: 1.4 },
        { text: "Display Ads", positions: { desktop: { top: "50%", left: "32%" }, mobile: { top: "27%", left: "24%" } }, delay: 1.2 },
        { text: "Destination Campaigns", positions: { desktop: { top: "78%", left: "9%" }, mobile: { top: "74%", left: "26%" } }, delay: 1.6 },
        { text: "Tourism Careers", positions: { desktop: { top: "90%", left: "52%" }, mobile: { top: "90%", left: "30%" } }, delay: 1.0 },

        // Right Column (Mobile Stack)
        { text: "Brand Stories", positions: { desktop: { top: "11%", left: "50%" }, mobile: { top: "10%", left: "74%" } }, delay: 0 },
        { text: "Super Deals", positions: { desktop: { top: "28%", left: "83%" }, mobile: { top: "27%", left: "76%" } }, delay: 0.4 },
        { text: "Tour Packages", positions: { desktop: { top: "50%", left: "68%" }, mobile: { top: "74%", left: "74%" } }, delay: 0.6 },
        { text: "Travel Web stories", positions: { desktop: { top: "78%", left: "92%" }, mobile: { top: "90%", left: "70%" } }, delay: 0.2 }
    ];

    return (
        <section id="feature360-section" className="w-full bg-[#000000] pt-5 md:pt-24 lg:pt-10 pb-4 lg:pb-0 px-0 sm:px-8 font-sans overflow-hidden">

            <style>{`
                /* Perfect, smooth, centered 360 rotation */
                @keyframes radar-spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>

            <div className="max-w-[1500px] mx-auto flex flex-col items-center">

                {/* --- TOP SECTION: Small Label & Main Headline --- */}
                <div className="w-full text-center flex flex-col items-center gap-4 md:gap-6 z-10 mb-0 sm:mb-14 px-4">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#d02525] border border-gray-800 mb-0 sm:mb-6 shadow-sm">
                        <span className="text-white font-poppins text-[10px] sm:text-xs md:text-sm font-semibold tracking-widest uppercase">
                            The Modern Travel Ecosystem
                        </span>
                    </div>
                    <h2 className="text-white text-[25px] sm:text-4xl md:text-4xl lg:text-[54px] font-medium leading-tight tracking-tight max-w-4xl whitespace-normal md:whitespace-nowrap md:-mt-[20px]">
                        Beyond Advertising & Visibility
                    </h2>
                    {/* <div className="w-20 sm:w-32 md:w-40 lg:w-20 h-1 bg-[#d02525] mt-0 mx-auto" aria-hidden="true"></div> */}
                    <p className="text-white text-[18px] sm:text-base md:text-2xl font-poppins lg:text-[24px] leading-relaxed max-w-4xl lg:max-w-6xl mt-1 sm:mt-1 whitespace-normal ">
                        Influencing how modern travelers discover destinations,
                        experiences, <br className="hidden lg:block" /> brands, and immersive travel journeys worldwide.
                    </p>
                </div>

                {/* Main 360 Orbital Graphic Container */}
                {/* MOBILE FIX: Changed height to an explicit tall h-[580px] on mobile to give room for the stack */}
                <div className="relative w-full max-w-[1700px] h-[580px] sm:h-[650px] md:h-auto md:aspect-[2.5/1] md:mb-20 flex items-center justify-center overflow-hidden md:overflow-visible mt-6 md:mt-0">

                    {/* 3D Perspective Master Wrapper */}
                    {/* MOBILE FIX: w-[240%] makes the rings massive on mobile (bleeding off screen). scale-y-[0.98] makes them round instead of squashed. */}
                    <div className="absolute top-1/2 left-1/2 w-[240%] sm:w-[150%] md:w-full aspect-square -translate-x-1/2 -translate-y-1/2 scale-y-[0.98] md:scale-y-[0.42] pointer-events-none z-0">

                        {/* 1. Base Rings SVG (z-0: BOTTOM LAYER) */}
                        <img
                            src={orbitRings}
                            alt="360 Orbit Rings"
                            className="absolute inset-0 w-full h-full object-contain z-0 pointer-events-none"
                            style={{ filter: 'grayscale(1) brightness(1) contrast(0.95)', opacity: 0.25 }}
                        />

                        {/* 2. EXACT SLICED RADAR WEDGE (z-10: MIDDLE LAYER) */}
                        <div
                            className="absolute inset-0 pointer-events-none z-10"
                            style={{
                                borderRadius: '50%',
                                overflow: 'visible',
                                animation: 'radar-spin 5s linear infinite',
                                transformOrigin: '50% 50%',
                                maskImage: 'conic-gradient(from -90deg, black 0deg 70deg, transparent 70deg 360deg)',
                                WebkitMaskImage: 'conic-gradient(from -90deg, black 0deg 70deg, transparent 70deg 360deg)'
                            }}
                            aria-hidden="true"
                        >
                            <div
                                className="absolute inset-0"
                                style={{
                                    background: `radial-gradient(circle at center,
                                        transparent 0%,
                                        transparent 14%,
                                        #ff7477 14% 28%,
                                        #ff8d8d 28% 42%,
                                        #ffaaad 42% 56%,
                                        #ffc7c8 56% 70%,
                                        #ffdddc 70% 84%,
                                        transparent 84% 100%
                                    )`,
                                    borderRadius: '50%',
                                    opacity: 10,
                                }}
                            />
                        </div>

                    </div>

                    {/* Center Core Logo */}
                    {/* MOBILE FIX: Increased base w-[100px] to make logo more prominent on mobile */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30 flex items-center justify-center">
                        <img
                            src={travelLogo}
                            alt="Brand Logo"
                            className="w-[100px] sm:w-[120px] md:w-[130px] lg:w-[156px] xl:w-[140px] h-auto object-contain bg-black"
                        />
                    </div>

                    {/* Floating Labels (z-20: TOP LAYER) */}
                    {labelsData.map((label, index) => (
                        <FloatingLabel
                            key={index}
                            text={label.text}
                            positions={label.positions}
                            delay={label.delay}
                            isMobile={isMobile}
                        />
                    ))}

                </div>

            </div>
        </section>
    );
};

export default React.memo(Feature360Section);